import { getPrivateMoneys } from 'admin/actions/private-moneys';
import {
  approveShopApplication,
  holdShopApplication,
  rejectShopApplication,
  reviewShopApplication,
  ShopApplication,
  ShopApplicationStatus,
  showShopApplication,
  updateShopApplication,
} from 'admin/actions/shop-applications';
import { showShop } from 'admin/actions/shops';
import { getActiveUserTagGroupsWithItems, NormalizedTagGroup } from 'admin/actions/user-tag-group';
import { fetchUserTags } from 'admin/actions/user-tags';
import { Button } from 'admin/components/atoms/Buttons';
import Grid from 'admin/components/atoms/Grid';
import Label from 'admin/components/atoms/Label';
import BreadCrumb, { CrumbType } from 'admin/components/molecules/BreadCrumb';
import {
  DecisionBar,
  DecisionButton,
  DecisionGuide,
  DiscardChangesModal,
  isReviewEditable,
  ReviewStepper,
  StepperHeader,
  TerminalNote,
} from 'admin/components/organisms/ReviewFlow';
import TagSelectionEditor, { TagSelection } from 'admin/components/organisms/TagSelectionEditor';
import { USAGE_TYPE_OPTIONS, UsageType } from 'admin/constants/usageType';
import { EXCLUDED_ISSUER_ORG_CODE } from 'admin/constants/userTagGroupLabels';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import NotificationToast from 'admin/components/molecules/NotificationToast';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { pickIssuerOrgCodeFromMoneys } from '~/javascripts/admin/utils/shopFormValidation';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import Select from '../../atoms/Select';
import TextBox from '../../atoms/TextBox';
import ConfirmModal, { ConfirmAction } from './ConfirmModal';

const CRUMBS: CrumbType[] = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '店舗管理', href: '', isCurrentPage: true },
  { value: '加盟店舗申請管理', href: '/shops/applications', isCurrentPage: false },
  { value: '加盟店舗申請詳細', href: '', isCurrentPage: true },
];

type OriginalShopData = {
  private_money_ids: string[];
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  usage_type: UsageType | '';
};

// 付与中の item id 群を、所属グループ単位の TagSelection に集約する。
const buildSelectionsFromItemIds = (
  groups: NormalizedTagGroup[],
  itemIds: string[]
): TagSelection[] =>
  groups
    .map((group) => ({
      groupId: group.id,
      itemIds: itemIds.filter((id) => group.items.some((it) => it.id === id)),
    }))
    .filter((row) => row.itemIds.length > 0);

const Wrapper = styled.div`
  margin-top: 24px;
`;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    ${(props) => props.theme.typography.body2.regular}
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const SectionLabel = styled(Label)`
  font-weight: 600;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DisplayText = styled.div`
  padding: 8px 0;
  color: #333;
`;


const DeleteBadgeContainer = styled.span`
  border-radius: 4px;
  background: #666;
  width: 78px;
  height: 45px;
  color: #fff;
  font-size: 14px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EditedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #21bf25;
  width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
  margin-left: 8px;
`;

const RevisedBadgeContainer = styled.span`
  border-radius: 4px;
  background: #f38d22;
  min-width: 64px;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  display: inline-block;
  margin-left: 8px;
`;

const EditedBadge = () => <EditedBadgeContainer>変更あり</EditedBadgeContainer>;
const DeleteBadge = () => <DeleteBadgeContainer>店舗廃止</DeleteBadgeContainer>;
const RevisedBadge = () => <RevisedBadgeContainer>修正あり</RevisedBadgeContainer>;

const LabelWithBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SectionLabelWithBadge = styled(LabelWithBadge)`
  display: block;
`;

type PrivateMoneyOption = {
  label: string;
  value: string;
};

type FormValues = {
  private_money_ids: string[];
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  usage_type: UsageType | '';
};

const ShopApplicationDetails = () => {
  const { id } = useParams<{ id: string }>();
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const [isLoading, setIsLoading] = useState(true);
  const [application, setApplication] = useState<ShopApplication | null>(null);
  const [originalShopData, setOriginalShopData] = useState<OriginalShopData | null>(null);
  // The application's proposed values (submitted values at load, saved values after Save).
  // Baseline for detecting the approver's revisions.
  const [submittedShopData, setSubmittedShopData] = useState<OriginalShopData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 確認モーダルで実行する操作（承認/却下/保存）。可逆遷移はモーダルを介さない。
  const [modalAction, setModalAction] = useState<ConfirmAction | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  // 変更の破棄（未保存の編集を最後の保存状態へ戻す）の確認ダイアログ
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  // Form state with react-hook-form
  const {
    control,
    watch,
    reset,
    formState: { isDirty: formIsDirty },
  } = useForm<FormValues>({
    defaultValues: {
      private_money_ids: [],
      shop_name: '',
      shop_name_furigana: '',
      postal_code: '',
      address: '',
      tel: '',
      email: '',
      contact_person_name: '',
      contact_person_name_furigana: '',
      usage_type: '',
    },
  });

  const formValues = watch();

  // Tag group state（全有効グループ＋付与選択。RHF外で管理）
  const [availableTagGroups, setAvailableTagGroups] = useState<NormalizedTagGroup[]>([]);
  const [tagSelections, setTagSelections] = useState<TagSelection[]>([]);
  // 保存済み(=申請の現在値)タグ。未保存判定(isDirty)のベースライン。
  const [savedTagSelections, setSavedTagSelections] = useState<TagSelection[]>([]);
  // 変更元店舗の現在タグ（update 申請の差分表示用）
  const [originalTagSelections, setOriginalTagSelections] = useState<TagSelection[]>([]);
  const [tagGroupsLoading, setTagGroupsLoading] = useState(false);

  const isIssuer = user.isIssuerOfMoney?.(userPrivateMoney);

  // Private money options fetched per application organization
  const [availableMoneys, setAvailableMoneys] = useState<PrivateMoneyOption[]>([]);
  const privateMoneyOptions = availableMoneys;

  // Load tag groups for the issuer organization and return them
  const loadTagGroups = useCallback(
    async (issuerOrgCode: string | null): Promise<NormalizedTagGroup[]> => {
      if (!issuerOrgCode) {
        setAvailableTagGroups([]);
        return [];
      }

      setTagGroupsLoading(true);

      try {
        const groups = await getActiveUserTagGroupsWithItems(issuerOrgCode);
        setAvailableTagGroups(groups);
        return groups;
      } catch (err) {
        console.error('Failed to fetch user tag groups', err);
        return [];
      } finally {
        setTagGroupsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const fetchApplication = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await showShopApplication(id);
        setApplication(response);

        // Fetch moneys available to the application's organization, excluding pocketchange
        let issuerOrgCode: string | null = null;
        if (response.organization_code) {
          try {
            const moneyRes = await getPrivateMoneys(
              `organization_code=${response.organization_code}&per_page=10000`
            );
            setAvailableMoneys(
              moneyRes.rows
                .filter((m) => m.organization?.code !== EXCLUDED_ISSUER_ORG_CODE)
                .map((m) => ({ label: m.name, value: m.id }))
            );
            issuerOrgCode = pickIssuerOrgCodeFromMoneys(moneyRes.rows);
          } catch (e) {
            console.error('Failed to fetch private moneys', e);
          }
        }

        // Load tag groups for the issuer
        let groups: NormalizedTagGroup[] = [];
        if (issuerOrgCode) {
          groups = await loadTagGroups(issuerOrgCode);
        }

        // 申請が提案する user_tag_group_items からIDを抽出してグループ単位に集約
        const userTagGroupItemIds = (response.user_tag_group_items || []).map((item) => item.id);
        const loadedSelections = buildSelectionsFromItemIds(groups, userTagGroupItemIds);
        setTagSelections(loadedSelections);
        setSavedTagSelections(loadedSelections);

        // Set form field values using reset
        const submittedValues: FormValues = {
          private_money_ids: response.private_money_ids || [],
          shop_name: response.shop_name || '',
          shop_name_furigana: response.shop_name_furigana || '',
          postal_code: response.postal_code || '',
          address: response.address || '',
          tel: response.tel || '',
          email: response.email || '',
          contact_person_name: response.contact_person_name || '',
          contact_person_name_furigana: response.contact_person_name_furigana || '',
          usage_type: response.usage_type ?? '',
        };
        reset(submittedValues);
        // Snapshot of the submitted values (baseline for approver-revision detection). All kinds.
        setSubmittedShopData(submittedValues);

        // For new requests, store the application's initial values so admin edits can be detected
        if (response.kind === 'new') {
          setOriginalTagSelections(buildSelectionsFromItemIds(groups, userTagGroupItemIds));
          setOriginalShopData({
            private_money_ids: response.private_money_ids || [],
            shop_name: response.shop_name || '',
            shop_name_furigana: response.shop_name_furigana || '',
            postal_code: response.postal_code || '',
            address: response.address || '',
            tel: response.tel || '',
            email: response.email || '',
            contact_person_name: response.contact_person_name || '',
            contact_person_name_furigana: response.contact_person_name_furigana || '',
            usage_type: response.usage_type ?? '',
          });
        }

        // Fetch original shop data for update requests
        if (response.kind === 'update' && response.target_shop_id) {
          try {
            const shop = await showShop(response.target_shop_id);
            // Filter Pokepay Money since it is a special money
            const shopPrivateMoneyIds =
              shop.accounts
                ?.filter((acc) => acc.private_money?.organization?.code !== 'pocketchange')
                ?.map((acc: any) => acc.private_money?.id)
                .filter(Boolean) || [];
            // 変更元店舗の現在タグを別途取得（showShop レスポンスには user_tag_group_items が含まれないため）
            const issuerAccount = (shop.accounts || []).find(
              (a: any) =>
                a.private_money?.organization?.code &&
                a.private_money.organization.code !== EXCLUDED_ISSUER_ORG_CODE
            );
            const tagsQuery = issuerAccount?.private_money?.id
              ? `private_money_id=${issuerAccount.private_money.id}`
              : '';
            const userTagsResponse = await fetchUserTags(response.target_shop_id, tagsQuery);
            const shopItemIds = userTagsResponse.rows.map((t) => t.user_tag_group_item.id);
            setOriginalTagSelections(buildSelectionsFromItemIds(groups, shopItemIds));
            setOriginalShopData({
              private_money_ids: shopPrivateMoneyIds,
              shop_name: shop.name || '',
              shop_name_furigana: shop.shop_name_furigana || '',
              postal_code: shop.postal_code || '',
              address: shop.address || '',
              tel: shop.tel || '',
              email: shop.email || '',
              contact_person_name: shop.contact_person_name || '',
              contact_person_name_furigana: shop.contact_person_name_furigana || '',
              usage_type: shop.usage_type ?? '',
            });
          } catch (shopErr) {
            console.error('Failed to fetch original shop data', shopErr);
          }
        }
      } catch (err) {
        console.error('Failed to fetch shop application', err);
        setError('申請情報の取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplication();
  }, [id, loadTagGroups, reset]);

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  // PATCH ボディを現在のフォーム値＋タグ選択から組み立てる。
  const buildUpdatePayload = (): Record<string, unknown> => {
    const userTagGroupItemIds = tagSelections.flatMap((row) => row.itemIds).filter(Boolean);
    return {
      private_money_ids: formValues.private_money_ids,
      shop_name: formValues.shop_name,
      shop_name_furigana: formValues.shop_name_furigana,
      postal_code: formValues.postal_code,
      address: formValues.address,
      tel: formValues.tel,
      email: formValues.email,
      contact_person_name: formValues.contact_person_name,
      contact_person_name_furigana: formValues.contact_person_name_furigana,
      // 未選択('')のときは null を送り据え置く（空文字はサーバで 422）。
      usage_type: formValues.usage_type || null,
      // 全解除（空配列）も「変更なし(未指定)」と区別して反映させるため、常に送信する。
      user_tag_group_item_ids: userTagGroupItemIds,
    };
  };

  // 遷移（と、保存/承認時の PATCH）を実行する中核。可逆遷移はモーダルなしでここへ直接来る。
  const runTransition = async (action: ConfirmAction) => {
    if (!id || !application) return;
    const fromStatus = application.status;

    setIsSubmitting(true);
    setSubmitResult(null);
    setSubmitMessage(null);

    // 内容(PATCH)を保存するのは「変更を保存」または承認のみ。廃止申請は編集内容が無いので PATCH しない。
    // 審査開始/保留は状態を移すだけ、却下は不備でも成立させたいため PATCH しない。
    const shouldPatch = (action === 'save' || action === 'approve') && application.kind !== 'disable';

    try {
      if (shouldPatch) {
        const updateResponse = await updateShopApplication(id, buildUpdatePayload());
        if (updateResponse.type) {
          setSubmitResult('error');
          setSubmitMessage(updateResponse.message || '申請の更新に失敗しました');
          setIsSubmitting(false);
          setIsConfirmModalOpen(false);
          return;
        }
      }

      if (action === 'save') {
        // 状態は変えずに内容だけ保存 → 再取得して未保存フラグを解消する。
        const refreshed = await showShopApplication(id);
        setApplication(refreshed);
        reset(formValues);
        setSavedTagSelections(tagSelections);
        // After saving, make the saved values the new baseline so "Revised" badges clear.
        setSubmittedShopData(formValues);
        setSubmitResult('success');
        setSubmitMessage('申請内容を保存しました');
        setIsSubmitting(false);
        setIsConfirmModalOpen(false);
        return;
      }

      const api: Record<'review' | 'hold' | 'approve' | 'reject', () => Promise<ShopApplication>> = {
        review: () => reviewShopApplication(id),
        hold: () => holdShopApplication(id),
        approve: () => approveShopApplication(id),
        reject: () => rejectShopApplication(id),
      };
      const response = await api[action as 'review' | 'hold' | 'approve' | 'reject']();

      if (response.type) {
        setSubmitResult('error');
        setSubmitMessage(response.message || 'ステータスの更新に失敗しました');
      } else {
        setSubmitResult('success');
        setApplication(response);
        setSubmitMessage(
          {
            // 受付済→審査中は「開始」、保留→審査中は「再開」（ボタン文言と一致させる）。
            review: fromStatus === 'pending' ? '審査を再開しました' : '審査を開始しました',
            hold: '申請を保留にしました',
            approve: '申請を承認しました',
            reject: '申請を却下しました',
          }[action as 'review' | 'hold' | 'approve' | 'reject']
        );
      }
    } catch (err: any) {
      setSubmitResult('error');
      setSubmitMessage(err?.message || 'ステータスの更新に失敗しました');
    } finally {
      setIsSubmitting(false);
      setIsConfirmModalOpen(false);
    }
  };

  // 可逆遷移（審査を開始 / 審査を再開 / 保留）はモーダルなしで即実行する。
  const startReview = () => runTransition('review');
  const hold = () => runTransition('hold');

  // 未保存の編集を破棄して最後に保存した状態へ戻す（フォーム項目 + 店舗ラベル）。
  const confirmDiscard = () => {
    reset(); // フォーム項目を最後の保存値（RHF の defaultValues）へ戻す
    setTagSelections(savedTagSelections); // 店舗ラベルをベースラインへ戻す
    setIsDiscardOpen(false);
  };

  // 承認 / 却下 / 変更を保存 は確認モーダルを経由する。
  const openModal = (action: ConfirmAction) => {
    setModalAction(action);
    setIsConfirmModalOpen(true);
  };
  const onConfirm = () => {
    if (modalAction) runTransition(modalAction);
  };

  if (isLoading) {
    return (
      <>
        <BreadCrumb crumbs={CRUMBS} />
        <Headings.h5>加盟店舗申請詳細</Headings.h5>
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      </>
    );
  }

  if (error || !application) {
    return (
      <>
        <BreadCrumb crumbs={CRUMBS} />
        <Headings.h5>加盟店舗申請詳細</Headings.h5>
        <Wrapper>
          <p>{error || '申請情報が見つかりません'}</p>
        </Wrapper>
      </>
    );
  }

  const currentStatus = application.status;
  const isDisableKind = application.kind === 'disable';
  // 内容編集は「審査中」のみ許可。廃止申請・非発行体・確定済みは読み取り専用。
  const isEditable = isReviewEditable(currentStatus);
  const fieldsDisabled = isDisableKind || !isIssuer || !isEditable;
  // 未保存の変更があるか（RHF管理外の店舗ラベル編集も含む）。
  const tagIds = (sel: TagSelection[]) => sel.flatMap((r) => r.itemIds).sort();
  const tagsDirty =
    JSON.stringify(tagIds(tagSelections)) !== JSON.stringify(tagIds(savedTagSelections));
  const isDirty = formIsDirty || tagsDirty;

  // Helper to check if arrays are equal
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  // 承認/却下済みの申請は履歴であり、現在店舗とのライブ差分表示は誤解を招くため
  // 「変更あり」バッジを一切出さない (#7068)
  const isDecided = application.status === 'approved' || application.status === 'rejected';

  // Generic per-field comparison between two shop-data snapshots.
  const fieldDiffers = (
    a: OriginalShopData,
    b: OriginalShopData,
    field: keyof OriginalShopData
  ): boolean =>
    field === 'private_money_ids'
      ? !arraysEqual(a.private_money_ids, b.private_money_ids)
      : a[field] !== b[field];

  // "Edited" = submitted value differs from the source shop (the applicant's change).
  // Update only — a new application has no source shop to compare against.
  // Shared by the form labels and the confirm modal.
  const isFieldChanged = (field: keyof OriginalShopData): boolean => {
    if (isDecided || application.kind !== 'update' || !submittedShopData || !originalShopData) {
      return false;
    }
    return fieldDiffers(submittedShopData, originalShopData, field);
  };

  // "Revised" = current form value differs from the submitted value (the approver's revision).
  // Shared by form labels and the modal (new + update).
  const isFieldRevised = (field: keyof OriginalShopData): boolean => {
    if (isDecided || application.kind === 'disable' || !submittedShopData) return false;
    return fieldDiffers(formValues, submittedShopData, field);
  };

  // Shop-label "Edited" = submitted (saved) selection differs from the source shop (applicant's change).
  // For form labels (update only).
  const tagsChanged =
    !isDecided &&
    application.kind === 'update' &&
    !!originalShopData &&
    !arraysEqual(
      savedTagSelections.flatMap((r) => r.itemIds),
      originalTagSelections.flatMap((r) => r.itemIds)
    );

  // Shop-label "Revised" = current selection differs from the submitted (saved) selection (approver's revision).
  // For form labels (new + update).
  const tagsRevised =
    !isDecided &&
    application.kind !== 'disable' &&
    !arraysEqual(
      tagSelections.flatMap((r) => r.itemIds),
      savedTagSelections.flatMap((r) => r.itemIds)
    );

  // Whether shop-label badges may appear at all (confirm modal: new + update).
  // The actual detection is done per group, so this only gates the whole section.
  const showTagBadges = !isDecided && application.kind !== 'disable' && !!originalShopData;
  // "Revised" shows even without a source shop (new is also in scope).
  const showTagRevised = !isDecided && application.kind !== 'disable';

  // Tag summary for the confirm modal: always show groups in (original ∪ submitted ∪ current),
  // deciding both flags per group. Build rows from the union so a fully-deleted group stays visible.
  const originalItemIdsByGroup = (groupId: string) =>
    originalTagSelections.find((r) => r.groupId === groupId)?.itemIds ?? [];
  const savedItemIdsByGroup = (groupId: string) =>
    savedTagSelections.find((r) => r.groupId === groupId)?.itemIds ?? [];
  const currentItemIdsByGroup = (groupId: string) =>
    tagSelections.find((r) => r.groupId === groupId)?.itemIds ?? [];

  const summaryGroupIds = Array.from(
    new Set(
      [...originalTagSelections, ...savedTagSelections, ...tagSelections]
        .map((r) => r.groupId)
        .filter((gid): gid is string => !!gid)
    )
  );

  const tagSummary = summaryGroupIds.map((groupId) => {
    const group = availableTagGroups.find((g) => g.id === groupId);
    const currentIds = currentItemIdsByGroup(groupId);
    const itemNames = currentIds
      .map((itemId) => group?.items.find((it) => it.id === itemId)?.name)
      .filter((n): n is string => !!n);
    // "Edited" = submitted (saved) selection ≠ source shop (update only).
    const changed =
      showTagBadges &&
      application.kind === 'update' &&
      !arraysEqual(savedItemIdsByGroup(groupId), originalItemIdsByGroup(groupId));
    // "Revised" = current ≠ submitted (saved) selection (approver's revision).
    const revised = showTagRevised && !arraysEqual(currentIds, savedItemIdsByGroup(groupId));
    return { groupId, groupName: group?.name ?? '', itemNames, changed, revised };
  });

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>加盟店舗申請詳細</Headings.h5>
      <Wrapper>
        {/* ステッパーと文脈アクション（審査を開始 / 審査を再開）を横並びに */}
        <StepperHeader>
          <ReviewStepper current={currentStatus} />
          {isIssuer && currentStatus === 'submitted' && (
            <Button type="button" variant="contained" onClick={startReview} disabled={isSubmitting}>
              審査を開始
            </Button>
          )}
          {isIssuer && currentStatus === 'pending' && (
            <Button type="button" variant="contained" onClick={startReview} disabled={isSubmitting}>
              審査を再開
            </Button>
          )}
        </StepperHeader>
        {isDecided && <TerminalNote status={currentStatus} entityLabel="店舗" />}
        <FormGrid>
          <Grid.Item size={2}>
            <SectionLabel>基本情報</SectionLabel>
          </Grid.Item>
          <Grid.Item size={8} container columnGap={32} rowGap={8}>
            {/* 申請番号 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <Label>申請番号</Label>
              <DisplayText>{application.id}</DisplayText>
            </Grid.Item>

            {isDisableKind && (
              <Grid.Item size={8} flexDirection="column" nextRow>
                <Label>変更内容</Label>
                <DeleteBadge />
              </Grid.Item>
            )}

            {/* 組織名 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <Label>組織名</Label>
              <TextBox syncValue={application.organization_code || ''} disabled fullWidth />
            </Grid.Item>

            {/* 加盟するマネー */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>加盟するマネー</Label>
                {isFieldChanged('private_money_ids') && <EditedBadge />}
                {isFieldRevised('private_money_ids') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="private_money_ids"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select<PrivateMoneyOption, true, never>
                    isMulti
                    options={privateMoneyOptions}
                    value={privateMoneyOptions.filter((opt) => value.includes(opt.value))}
                    onChange={(opts) => onChange(opts?.map((opt) => opt.value) ?? [])}
                    isDisabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 店舗名 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>店舗名</Label>
                {isFieldChanged('shop_name') && <EditedBadge />}
                {isFieldRevised('shop_name') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="shop_name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 店舗名（フリガナ） */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>店舗名（フリガナ）</Label>
                {isFieldChanged('shop_name_furigana') && <EditedBadge />}
                {isFieldRevised('shop_name_furigana') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="shop_name_furigana"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 郵便番号 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>郵便番号</Label>
                {isFieldChanged('postal_code') && <EditedBadge />}
                {isFieldRevised('postal_code') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="postal_code"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 住所 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>住所</Label>
                {isFieldChanged('address') && <EditedBadge />}
                {isFieldRevised('address') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="address"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 電話番号 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>電話番号</Label>
                {isFieldChanged('tel') && <EditedBadge />}
                {isFieldRevised('tel') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="tel"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* メールアドレス */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>メールアドレス</Label>
                {isFieldChanged('email') && <EditedBadge />}
                {isFieldRevised('email') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="email"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 担当者名 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>担当者名</Label>
                {isFieldChanged('contact_person_name') && <EditedBadge />}
                {isFieldRevised('contact_person_name') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="contact_person_name"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 担当者名（フリガナ） */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>担当者名（フリガナ）</Label>
                {isFieldChanged('contact_person_name_furigana') && <EditedBadge />}
                {isFieldRevised('contact_person_name_furigana') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="contact_person_name_furigana"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextBox
                    syncValue={value}
                    onChange={onChange}
                    fullWidth
                    disabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>

            {/* 店舗形態 */}
            <Grid.Item size={8} flexDirection="column" nextRow>
              <LabelWithBadge>
                <Label>店舗形態</Label>
                {isFieldChanged('usage_type') && <EditedBadge />}
                {isFieldRevised('usage_type') && <RevisedBadge />}
              </LabelWithBadge>
              <Controller
                name="usage_type"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select<{ value: UsageType; label: string }, false, never>
                    options={USAGE_TYPE_OPTIONS}
                    value={USAGE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                    onChange={(opt) => onChange(opt ? opt.value : '')}
                    placeholder="店舗形態を選択してください"
                    isDisabled={fieldsDisabled}
                  />
                )}
              />
            </Grid.Item>
          </Grid.Item>

          <Grid.Divider />

          <Grid.Item size={2}>
            <SectionLabelWithBadge>
              <SectionLabel>店舗ラベル</SectionLabel>
              {tagsChanged && <EditedBadge />}
              {tagsRevised && <RevisedBadge />}
            </SectionLabelWithBadge>
          </Grid.Item>
          <Grid.Item size={10} flexDirection="column">
            {tagGroupsLoading ? (
              <div>読み込み中...</div>
            ) : (
              <TagSelectionEditor
                groups={availableTagGroups}
                value={tagSelections}
                onChange={setTagSelections}
                disabled={fieldsDisabled}
              />
            )}
          </Grid.Item>

        </FormGrid>

        {/* 結果フィードバックは画面上部の固定トースト（3 秒で自動クローズ・× で即閉じ）。 */}
        <NotificationToast
          isOpen={submitResult !== null}
          variant={submitResult === 'error' ? 'error' : 'success'}
          message={submitMessage}
          onClose={() => {
            setSubmitResult(null);
            setSubmitMessage(null);
          }}
        />

        {/* 審査中のみ: すべて読んだうえで保存・保留・判定する下部固定バー */}
        {isIssuer && currentStatus === 'reviewing' && (
          <DecisionBar>
            <DecisionGuide>
              {isDirty
                ? '未保存の変更があります。「変更を保存」するか「変更を破棄」してください。'
                : '全ての情報を確認してから承認/却下してください'}
            </DecisionGuide>
            {!isDisableKind && (
              <DecisionButton
                type="button"
                variant="muted-outline"
                onClick={() => setIsDiscardOpen(true)}
                disabled={!isDirty || isSubmitting}
              >
                変更を破棄
              </DecisionButton>
            )}
            {!isDisableKind && (
              <DecisionButton
                type="button"
                variant="outline"
                onClick={() => openModal('save')}
                disabled={!isDirty || isSubmitting}
              >
                変更を保存
              </DecisionButton>
            )}
            <DecisionButton
              type="button"
              variant="muted-outline"
              onClick={hold}
              disabled={isDirty || isSubmitting}
            >
              保留
            </DecisionButton>
            <DecisionButton
              type="button"
              variant="danger"
              onClick={() => openModal('reject')}
              disabled={isDirty || isSubmitting}
            >
              却下
            </DecisionButton>
            <DecisionButton
              type="button"
              variant="contained"
              onClick={() => openModal('approve')}
              disabled={isDirty || isSubmitting}
            >
              承認
            </DecisionButton>
          </DecisionBar>
        )}
      </Wrapper>

      <DiscardChangesModal
        isOpen={isDiscardOpen}
        onCancel={() => setIsDiscardOpen(false)}
        onConfirm={confirmDiscard}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        action={modalAction ?? 'save'}
        organizationCode={application.organization_code}
        privateMoneyNames={privateMoneyOptions
          .filter((opt) => formValues.private_money_ids.includes(opt.value))
          .map((opt) => opt.label)}
        shopName={formValues.shop_name}
        shopNameFurigana={formValues.shop_name_furigana}
        postalCode={formValues.postal_code}
        address={formValues.address}
        tel={formValues.tel}
        email={formValues.email}
        contactPersonName={formValues.contact_person_name}
        contactPersonNameFurigana={formValues.contact_person_name_furigana}
        usageType={formValues.usage_type}
        tagSummary={tagSummary}
        changedFields={{
          private_money_ids: isFieldChanged('private_money_ids'),
          shop_name: isFieldChanged('shop_name'),
          shop_name_furigana: isFieldChanged('shop_name_furigana'),
          postal_code: isFieldChanged('postal_code'),
          address: isFieldChanged('address'),
          tel: isFieldChanged('tel'),
          email: isFieldChanged('email'),
          contact_person_name: isFieldChanged('contact_person_name'),
          contact_person_name_furigana: isFieldChanged('contact_person_name_furigana'),
          usage_type: isFieldChanged('usage_type'),
        }}
        revisedFields={{
          private_money_ids: isFieldRevised('private_money_ids'),
          shop_name: isFieldRevised('shop_name'),
          shop_name_furigana: isFieldRevised('shop_name_furigana'),
          postal_code: isFieldRevised('postal_code'),
          address: isFieldRevised('address'),
          tel: isFieldRevised('tel'),
          email: isFieldRevised('email'),
          contact_person_name: isFieldRevised('contact_person_name'),
          contact_person_name_furigana: isFieldRevised('contact_person_name_furigana'),
          usage_type: isFieldRevised('usage_type'),
        }}
        onClose={handleCloseConfirmModal}
        onConfirm={onConfirm}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default ShopApplicationDetails;
